import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18next from 'i18next';
import get from 'lodash.get';

import ButtonHoverAnimation from 'components/ButtonHoverAnimation';

import {
  package as packagePropType,
  packageContext as packageContextTypes,
} from 'lib/CustomPropTypes';
import { fetchLatestCardsForPkg } from 'redux/modules/liveBlog';
import { getLiveBlogArticleId, querySettings } from 'lib/liveBlogPkgHelpers';

import CardList from './CardList';

const {
  LIVE_BLOG_POLLING_INTERVAL = 30000,
} = process.env;

const mapStateToProps = ({
  liveBlog,
}) => ({
  liveBlogPkgInstances: liveBlog.pkgInstances,
});

@connect(mapStateToProps)
class LiveBlog extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    isEmbedded: PropTypes.bool,
    content: packagePropType,
    liveBlogPkgInstances: PropTypes.objectOf(PropTypes.any),
  };

  static contextTypes = {
    store: PropTypes.objectOf(PropTypes.any),
    ...packageContextTypes,
  };

  static defaultProps = {
    isEmbedded: false,
    content: {},
    liveBlogPkgInstances: {},
  };

  constructor(props) {
    super(props);

    const { content: packageData } = props;
    this.state = {
      articleId: getLiveBlogArticleId(packageData),
    };
  }

  componentDidMount() {
    this.startPolling();
  }

  static getDerivedStateFromProps(props, state) {
    const { content: packageData } = props;
    const { articleId: prevArticleId } = state;
    const articleId = getLiveBlogArticleId(packageData);

    // Reset state if live blog article id is changed
    if (prevArticleId !== articleId) {
      return {
        articleId,
      };
    }
    return null;
  }

  componentWillUnmount() {
    if (this.cardPollInterval) {
      clearInterval(this.cardPollInterval);
    }
  }

  startPolling = () => {
    // Interval cleared in componentWillUnmount
    this.cardPollInterval = setInterval(this.queryCards, LIVE_BLOG_POLLING_INTERVAL);
  };

  queryCards = () => {
    const { page, queryLimit, shownOnFronts } = querySettings;
    const { articleId } = this.state;
    const { dispatch } = this.props;
    const fetchingLatest = get(this.props, `liveBlogPkgInstances.${articleId}.fetchingLatest`, false);

    if (!fetchingLatest) {
      dispatch(fetchLatestCardsForPkg({
        articleId,
        queryLimit,
        page,
        shownOnFronts,
      }));
    }
  };

  renderButton = (itemUrl) => {
    const packageMetadata = get(this.props, 'content.metadata') || {};
    const {
      seeAllText,
      seeAllUrl,
    } = packageMetadata;

    return (
      <div className="live-blog__button-wrapper">
        <ButtonHoverAnimation
          size="small"
          type="link"
          url={seeAllUrl || itemUrl}
          additionalClasses="live-blog__button"
          title={seeAllText || i18next.t('See All')}
        />
      </div>
    );
  };

  render() {
    const { articleId } = this.state;
    const { railContext } = this.context;
    const {
      content,
      isEmbedded,
      liveBlogPkgInstances,
    } = this.props;

    const item = get(content, 'items[0]') || {};
    const { id: itemId, type: itemType, metadata } = item;
    if (itemType !== 'liveBlog') return null;

    /**
     * On initial render, we get cards from the Curation API.
     * We then order the cards as follows:
     * pinned cards first
     * all other cards after that
     *
     * We consider cards with autofill=false to be "pinned"
     */
    const cardsFromCurator = get(item, 'related', []).filter((c) => c !== null);
    const pinnedCardsFromCurator = cardsFromCurator.filter((c) => !c.autofilled);
    const nonPinnedCardsFromCurator = cardsFromCurator.filter((c) => c.autofilled);
    const orderedCardsFromCurator = [...pinnedCardsFromCurator, ...nonPinnedCardsFromCurator];

    /**
     * Via polling we get an updated list of live blog cards direct
     * from the Bento API. Here we need merge the new cards from
     * Bento API with the existing cards from the Curation
     */
    let cardsToRender;
    const cardsFromBento = get(liveBlogPkgInstances, `${articleId}.latestItems`, []);
    if (cardsFromBento.length > 0) {
      const nonPinnedCardsFromCuratorById = nonPinnedCardsFromCurator
        .reduce((accumulator, card) => {
          accumulator[card.id] = card;
          return accumulator;
        }, {});

      const mergedCards = cardsFromBento.map((cardFromBento) => {
        const weAlreadyHaveCardFromCurator = !!nonPinnedCardsFromCuratorById[cardFromBento.id];
        if (weAlreadyHaveCardFromCurator) {
          return nonPinnedCardsFromCuratorById[cardFromBento.id];
        }
        return cardFromBento;
      });

      cardsToRender = [...pinnedCardsFromCurator, ...mergedCards];
    } else {
      cardsToRender = orderedCardsFromCurator;
    }

    const packageId = get(content, 'id');
    const packageType = get(content, 'type');

    // Is this a root package of type "Live Blog"?
    // (ex. this is false when embedded within "Cover Spread")
    const isLiveBlogPackage = packageType === 'postList';

    // Package metadata
    const { title, description } = metadata || {};
    const hasHeader = title || description;
    const itemUrl = get(item, 'computedValues.url');

    const isInRail = railContext !== null;

    return (
      <div
        // use package class only if root package
        className={classNames('live-blog', {
          'pkg live-blog--standalone': isLiveBlogPackage,
          [`live-blog--${packageType}`]: !isLiveBlogPackage,

          // Modify styling based on placement in rail
          'live-blog--in-rail': isInRail,
        })}
        data-test="live-blog"
        data-packageid={packageId}
      >
        { hasHeader && <div className="live-blog__top-decoration" /> }
        <div className="live-blog__content-wrapper">
          <div className="live-blog__content">
            <CardList
              id={itemId}
              content={item}
              cards={cardsToRender}
              isEmbedded={isEmbedded}
              defaultBreakingStyle={!isLiveBlogPackage} // default is not breaking if root package
              defaultLabel={isLiveBlogPackage ? '' : 'Live Blog'} // default is empty if root package
            />
          </div>
          {
            // "Cover Spread" does not support button yet
            isLiveBlogPackage && this.renderButton(itemUrl)
          }
        </div>
      </div>
    );
  }
}

export default LiveBlog;
